import { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import SocialApi from '../../api/social';

const POLL = 3000;
const FAILED_MESSAGE = 'Some connections could not be synced. Try again in a while.';

/**
 * Checks the user's connections and finds their ad accounts, in the background (it carries on if the page is left, and
 * shows again on return). onDone runs when it ends, so the page can reload; onError(text) gets what went wrong.
 */
export default function SyncNowButton({ onDone, onError }) {
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let current = true;
    SocialApi.syncStatus().then((status) => current && setSyncing(status.syncing)).catch(() => {});
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    if (!syncing) return undefined;
    const timer = setInterval(() => {
      SocialApi.syncStatus().then((status) => {
        if (status.syncing) return;
        setSyncing(false);
        onDone();
        if (status.failed) onError(FAILED_MESSAGE);
      }).catch(() => {});
    }, POLL);
    return () => clearInterval(timer);
  }, [syncing, onDone, onError]);

  const start = useCallback(async () => {
    setSyncing(true);
    try {
      await SocialApi.syncConnections();
    } catch (e) {
      setSyncing(false);
      onError(e.message);
    }
  }, [onError]);

  return (
    <Tooltip title="Check your connections and find their ad accounts">
      <span>
        <Button size="small" variant="contained" onClick={start} disabled={syncing} sx={{ whiteSpace: 'nowrap' }}
                startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
      </span>
    </Tooltip>
  );
}
